/**
 * Funding allocation for the jury vote (`juryVotingType: 'funding'`).
 *
 * The funding options are a multiset: the same amount may be configured more than once, and every
 * entry is one assignable slot. Both products share this semantics — Cassiopeia uses it to render
 * the award dropdown, Bigfoot additionally to decide whether the vote may be confirmed.
 */
/**
 * Counts how often each amount occurs in the given list.
 *
 * @param amounts Amounts to count.
 * @returns Amount → number of occurrences.
 */
export const countByAmount = (amounts) => amounts.reduce((acc, amount) => {
    acc[amount] = (acc[amount] || 0) + 1;
    return acc;
}, {});
/**
 * Amounts already assigned to all *other* projects. The project being edited is excluded so its own
 * slot stays available while the juror changes it.
 *
 * @param poll      `projectId → amount` map.
 * @param projectId Project whose slot is being edited.
 * @returns The amounts of the other projects.
 */
export const takenAmountsForProject = (poll, projectId) => Object.entries(poll)
    .filter(([pid]) => pid !== projectId)
    .map(([, amount]) => amount);
/**
 * Marks the funding options that are already used up by other projects. Options are walked in
 * configuration order, so the *n*-th duplicate of an amount is disabled only when *n* projects
 * already hold that amount.
 *
 * @param fundingOptions     Configured options, in display order.
 * @param takenCountByAmount Amount → number of allocations held by other projects.
 * @returns One entry per option, in the original order.
 */
export const resolveFundingOptionAvailability = (fundingOptions, takenCountByAmount) => {
    const usedCountByAmount = {};
    return fundingOptions.map((opt, idx) => {
        usedCountByAmount[opt.amount] = usedCountByAmount[opt.amount] || 0;
        const disabled = usedCountByAmount[opt.amount] < (takenCountByAmount[opt.amount] || 0);
        if (disabled)
            usedCountByAmount[opt.amount]++;
        return { idx, opt, disabled };
    });
};
/**
 * Whether every configured funding option has been assigned.
 *
 * @param assignedCount Number of projects with an allocation.
 * @param totalCount    Number of configured funding options.
 * @returns `true` only when all slots are taken and at least one slot exists.
 */
export const isFundingFullyAssigned = (assignedCount, totalCount) => assignedCount === totalCount && totalCount > 0;
//# sourceMappingURL=funding.js.map